import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import axios from "axios";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Services from "./pages/Services";
import Projects from "./pages/Projects";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Admin from "./pages/Admin";
import AuthCallback from "./pages/AuthCallback";
import { AuthProvider } from "./lib/AuthContext";

const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
};

const getSessionId = () => {
    let sid = localStorage.getItem("mwc_sid");
    if (!sid) {
        sid = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem("mwc_sid", sid);
    }
    return sid;
};

const VisitTracker = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        if (pathname.startsWith("/mwc-admin")) return;
        axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
            path: pathname,
            referrer: document.referrer || "",
            session_id: getSessionId(),
        }).catch(() => { /* silent */ });
    }, [pathname]);
    return null;
};

const MetaPixelTracker = () => {
    const { pathname } = useLocation();
    const isFirstRender = useRef(true);
    useEffect(() => {
        // Initial page load PageView is fired by the base snippet in index.html
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (pathname.startsWith("/mwc-admin")) return;
        if (typeof window.fbq === "function") {
            window.fbq("track", "PageView");
        }
    }, [pathname]);
    return null;
};

const SiteChrome = ({ children }) => {
    const { pathname } = useLocation();
    const isAdmin = pathname.startsWith("/mwc-admin");
    return (
        <>
            {!isAdmin && <Navbar />}
            {children}
            {!isAdmin && <Footer />}
        </>
    );
};

const AppRouter = () => {
    const location = useLocation();
    // Detect Emergent Auth callback synchronously (must happen before ScrollToTop / other routes)
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <>
            <ScrollToTop />
            <VisitTracker />
            <MetaPixelTracker />
            <SiteChrome>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/mwc-admin" element={<Admin />} />
                </Routes>
            </SiteChrome>
        </>
    );
};

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <AppRouter />
                    <Toaster position="bottom-right" />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
